"use client";

import { useState } from "react";
import { VacancyDelta } from "@/components/ui/VacancyDelta";
import { windowedPageItems, type PageToken } from "@/lib/pagination-window";

const PER_PAGE = 6;

interface HistoryItem {
  id: number;
  roomName: string | null;
  type: string | null;
  rent: string | null;
  eventType: string;
  detectedAt: string | Date;
}

export function VacancyHistoryPaginated({
  history,
  rentLabel = "家賃",
}: {
  history: HistoryItem[];
  /** 都民住宅は「家賃」ではなく「使用料」 */
  rentLabel?: string;
}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(history.length / PER_PAGE);
  const items = history.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <>
      {/* PC: テーブル表示 */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-blue-50 text-left">
              <th className="px-4 py-3 font-semibold">更新日時</th>
              <th className="px-4 py-3 font-semibold">部屋</th>
              <th className="px-4 py-3 font-semibold">タイプ</th>
              <th className="px-4 py-3 font-semibold">{rentLabel}</th>
              <th className="px-4 py-3 font-semibold text-center">変動</th>
            </tr>
          </thead>
          <tbody>
            {items.map((h) => (
              <tr
                key={h.id}
                className="border-b border-border even:bg-gray-50/50"
              >
                <td className="px-4 py-3 whitespace-nowrap text-muted">
                  {new Date(h.detectedAt).toLocaleString("ja-JP", {
                    timeZone: "Asia/Tokyo",
                  })}
                </td>
                <td className="px-4 py-3">{h.roomName || "-"}</td>
                <td className="px-4 py-3">{h.type || "-"}</td>
                <td className="px-4 py-3">{h.rent || "-"}</td>
                <td className="px-4 py-3 text-center">
                  <VacancyDelta
                    eventType={h.eventType as "appeared" | "disappeared"}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* スマホ: リスト表示 */}
      <div className="md:hidden divide-y divide-border">
        {items.map((h) => (
          <div key={h.id} className="py-3 px-1">
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium text-sm">
                {h.roomName || "-"}
              </span>
              <span className="shrink-0">
                <VacancyDelta
                  eventType={h.eventType as "appeared" | "disappeared"}
                />
              </span>
            </div>
            <p className="text-xs text-muted mt-1">
              {h.type || "-"} / {h.rent || "-"}
            </p>
            <p className="text-xs text-muted mt-0.5">
              {new Date(h.detectedAt).toLocaleString("ja-JP", {
                timeZone: "Asia/Tokyo",
                month: "numeric",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        ))}
      </div>

      {/* ページネーション。スマホはカード幅に収まる窓表示、md以上は全ページ番号 */}
      {totalPages > 1 && (
        <>
          <HistoryPager
            page={page}
            totalPages={totalPages}
            onPage={setPage}
            items={windowedPageItems(page + 1, totalPages)}
            compact
          />
          <HistoryPager
            page={page}
            totalPages={totalPages}
            onPage={setPage}
            items={Array.from({ length: totalPages }, (_, i) => i + 1)}
          />
        </>
      )}
    </>
  );
}

function HistoryPager({
  page,
  totalPages,
  onPage,
  items,
  compact = false,
}: {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  items: PageToken[];
  compact?: boolean;
}) {
  const current = page + 1;
  const control = compact
    ? "inline-flex shrink-0 items-center justify-center min-w-7 h-8 px-0 text-xs leading-none rounded border"
    : "px-3 py-1.5 text-sm rounded border";
  const idle =
    "border-border hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed";

  return (
    <nav
      aria-label="更新履歴のページ"
      className={
        compact
          ? "flex md:hidden items-center justify-center gap-px mt-4 max-w-full"
          : "hidden md:flex items-center justify-center gap-1 mt-4"
      }
    >
      <button
        type="button"
        onClick={() => onPage(Math.max(0, page - 1))}
        disabled={page === 0}
        aria-label="前のページ"
        className={`${control} ${idle}`}
      >
        {compact ? "‹" : "前へ"}
      </button>
      {items.map((item, index) =>
        item === "ellipsis" ? (
          <span
            key={`ellipsis-${index}`}
            aria-hidden
            className="w-3 shrink-0 text-center text-xs leading-none text-muted select-none"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onPage(item - 1)}
            aria-label={`${item}ページ`}
            aria-current={item === current ? "page" : undefined}
            className={`${control} ${
              item === current
                ? "bg-accent text-white border-accent"
                : idle
            }`}
          >
            {item}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onPage(Math.min(totalPages - 1, page + 1))}
        disabled={page === totalPages - 1}
        aria-label="次のページ"
        className={`${control} ${idle}`}
      >
        {compact ? "›" : "次へ"}
      </button>
    </nav>
  );
}
